import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useColorScheme } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ============================================================
// Theme Types
// ============================================================

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeColors {
  // Brand Primary
  primary: string;
  primaryHover: string;
  primaryLight: string;

  // Secondary (Blue - Links, Info)
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;

  // Semantic Colors
  success: string;
  successHover: string;
  successLight: string;

  warning: string;
  warningHover: string;
  warningLight: string;

  danger: string;
  dangerHover: string;
  dangerLight: string;

  info: string;
  infoLight: string;

  // Core Colors
  white: string;
  black: string;

  // Background Colors
  background: string;
  backgroundAlt: string;
  backgroundElevated: string;

  // Card Colors
  card: string;
  cardBorder: string;

  // Border Colors
  border: string;
  borderLight: string;
  inputBorder: string;
  divider: string;

  // Text Colors
  text: string;
  textPrimary: string;
  textSecondary: string;
  textBody: string;
  textMuted: string;
  textLight: string;
  textDisabled: string;
  textInverse: string;

  // Icon Colors
  iconDefault: string;
  iconMuted: string;
  iconLight: string;

  // Neutral Scale
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  neutral800: string;
  neutral900: string;

  // Gray Scale
  gray50: string;
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray800: string;
  gray900: string;

  // Slate Scale
  slate100: string;
  slate200: string;
  slate300: string;
  slate400: string;
  slate500: string;
  slate600: string;
  slate700: string;
}

export interface Theme {
  mode: 'light' | 'dark';
  colors: ThemeColors;
}

// ============================================================
// Light Theme Colors
// ============================================================

const lightColors: ThemeColors = {
  // Brand Primary
  primary: '#A9211F',
  primaryHover: '#8B1A18',
  primaryLight: '#FEF2F2',

  // Secondary (Blue - Links, Info)
  secondary: '#3B82F6',
  secondaryHover: '#2563EB',
  secondaryLight: '#EFF6FF',

  // Semantic Colors
  success: '#22C55E',
  successHover: '#16A34A',
  successLight: '#DCFCE7',

  warning: '#EAB308',
  warningHover: '#CA8A04',
  warningLight: '#FEF9C3',

  danger: '#EF4444',
  dangerHover: '#DC2626',
  dangerLight: '#FEF2F2',

  info: '#3B82F6',
  infoLight: '#DBEAFE',

  // Core Colors
  white: '#FFFFFF',
  black: '#0A0A0A',

  // Background Colors
  background: '#FAFAFA',
  backgroundAlt: '#F1F5F9',
  backgroundElevated: '#FFFFFF',

  // Card Colors
  card: '#FFFFFF',
  cardBorder: '#E5E7EB',

  // Border Colors
  border: '#E5E5E5',
  borderLight: '#E2E8F0',
  inputBorder: '#D4D4D4',
  divider: '#F3F4F6',

  // Text Colors
  text: '#171717',
  textPrimary: '#111827',
  textSecondary: '#414042',
  textBody: '#525252',
  textMuted: '#6B7280',
  textLight: '#9CA3AF',
  textDisabled: '#A3A3A3',
  textInverse: '#FFFFFF',

  // Icon Colors
  iconDefault: '#414042',
  iconMuted: '#6B7280',
  iconLight: '#9CA3AF',

  // Neutral Scale
  neutral50: '#FAFAFA',
  neutral100: '#F5F5F5',
  neutral200: '#E5E5E5',
  neutral300: '#D4D4D4',
  neutral400: '#A3A3A3',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral700: '#404040',
  neutral800: '#262626',
  neutral900: '#171717',

  // Gray Scale
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',

  // Slate Scale
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
};

// ============================================================
// Dark Theme Colors
// ============================================================

const darkColors: ThemeColors = {
  // Brand Primary (slightly lighter for dark mode)
  primary: '#C9423F',
  primaryHover: '#A9211F',
  primaryLight: '#3D1A1A',

  // Secondary (Blue - Links, Info)
  secondary: '#60A5FA',
  secondaryHover: '#3B82F6',
  secondaryLight: '#1E3A5F',

  // Semantic Colors
  success: '#4ADE80',
  successHover: '#22C55E',
  successLight: '#14532D',

  warning: '#FACC15',
  warningHover: '#EAB308',
  warningLight: '#422006',

  danger: '#F87171',
  dangerHover: '#EF4444',
  dangerLight: '#450A0A',

  info: '#60A5FA',
  infoLight: '#1E3A5F',

  // Core Colors
  white: '#FFFFFF',
  black: '#0A0A0A',

  // Background Colors
  background: '#0F0F0F',
  backgroundAlt: '#171717',
  backgroundElevated: '#1F1F1F',

  // Card Colors
  card: '#1A1A1A',
  cardBorder: '#2D2D2D',

  // Border Colors
  border: '#2D2D2D',
  borderLight: '#3D3D3D',
  inputBorder: '#404040',
  divider: '#262626',

  // Text Colors
  text: '#F5F5F5',
  textPrimary: '#FAFAFA',
  textSecondary: '#E5E5E5',
  textBody: '#D4D4D4',
  textMuted: '#A3A3A3',
  textLight: '#737373',
  textDisabled: '#525252',
  textInverse: '#171717',

  // Icon Colors
  iconDefault: '#E5E5E5',
  iconMuted: '#A3A3A3',
  iconLight: '#737373',

  // Neutral Scale (inverted for dark mode)
  neutral50: '#171717',
  neutral100: '#262626',
  neutral200: '#404040',
  neutral300: '#525252',
  neutral400: '#737373',
  neutral500: '#A3A3A3',
  neutral600: '#D4D4D4',
  neutral700: '#E5E5E5',
  neutral800: '#F5F5F5',
  neutral900: '#FAFAFA',

  // Gray Scale (inverted for dark mode)
  gray50: '#111827',
  gray100: '#1F2937',
  gray200: '#374151',
  gray300: '#4B5563',
  gray400: '#6B7280',
  gray500: '#9CA3AF',
  gray600: '#D1D5DB',
  gray700: '#E5E7EB',
  gray800: '#F3F4F6',
  gray900: '#F9FAFB',

  // Slate Scale (inverted for dark mode)
  slate100: '#334155',
  slate200: '#475569',
  slate300: '#64748B',
  slate400: '#94A3B8',
  slate500: '#CBD5E1',
  slate600: '#E2E8F0',
  slate700: '#F1F5F9',
};

// ============================================================
// Theme Context
// ============================================================

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  isDark: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  colors: ThemeColors;
}

const THEME_STORAGE_KEY = '@sms_theme_mode';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================
// Theme Provider
// ============================================================

export function ThemeProvider({ children }: { children: ReactNode }) {
  const systemColorScheme = useColorScheme();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load saved theme preference on mount
  useEffect(() => {
    loadThemePreference();
  }, []);

  const loadThemePreference = async () => {
    try {
      const savedMode = await AsyncStorage.getItem(THEME_STORAGE_KEY);
      if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
        setThemeModeState(savedMode as ThemeMode);
      }
    } catch (error) {
      console.log('Error loading theme preference:', error);
    } finally {
      setIsLoaded(true);
    }
  };

  const setThemeMode = async (mode: ThemeMode) => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.log('Error saving theme preference:', error);
    }
  };

  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
  };

  // Calculate the effective theme based on mode and system preference
  const effectiveTheme = useMemo((): 'light' | 'dark' => {
    if (themeMode === 'system') {
      return systemColorScheme === 'dark' ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, systemColorScheme]);

  const isDark = effectiveTheme === 'dark';
  const colors = isDark ? darkColors : lightColors;

  const theme: Theme = useMemo(() => ({
    mode: effectiveTheme,
    colors,
  }), [effectiveTheme, colors]);

  const contextValue = useMemo(() => ({
    theme,
    themeMode,
    isDark,
    setThemeMode,
    toggleTheme,
    colors,
  }), [theme, themeMode, isDark, colors]);

  // Don't render until theme preference is loaded
  if (!isLoaded) {
    return null;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================
// useTheme Hook
// ============================================================

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Export colors for direct access when needed
export { lightColors, darkColors };
